/**
 * Which rows a view shows.
 *
 * The conditions are combined by one connective for the whole list -- every one
 * of them must hold, or any one of them must -- which is what `filter_type` on
 * the view means. The connective is offered on the second row, because that is
 * the first row where it decides anything; the rows after it show it as text,
 * so it is stated without being asked three times.
 *
 * **Conditions nest.** A group is one more thing the connective combines, and
 * has a connective of its own for what is inside it -- which is how "A and (B
 * or C)" is written. The tree is built by `buildFilterTree`, the same function
 * that decides whether a row matches, so the shape somebody is editing and the
 * shape the rows are chosen by cannot disagree.
 */
import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewFilter } from '@/api/types'
import type { RowsView } from '../views/field-order'
import { useFieldContext } from '../fields/ui/context'
import { canFilterField, filterFor, filterTypesFor } from '../filters/inputs'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Switch } from '@/shared/ui/switch'
import { buildFilterTree, type TreeNode } from '../rows/rowMatch'
import { ConditionsNode, type ConditionId } from './condition-tree'
import { useViewFilters } from './filter-mutations'

/** One node of the tree, with ids on it, as this editor reads it. */
type FilterNode = TreeNode<ViewFilter>

/**
 * The row a condition or a group is stored as.
 *
 * The shared tree names them `number | string`, because the same component
 * edits a colour's conditions, which live inside one JSON column and are named
 * by whoever wrote them. A view's are rows the database numbers, so a string
 * here is not one of this view's and there is nothing to change.
 */
function storedId(id: ConditionId): number | null {
  return typeof id === 'number' ? id : null
}

export function FilterMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: RowsView
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const context = useFieldContext(fields[0]?.workspace_id)
  const { add, change, remove, addGroup, changeGroup, removeGroup, settings } =
    useViewFilters(tableId, view.id)

  const all = view.filters ?? []
  const filterable = fields.filter((field) => canFilterField(field))
  const disabled = view.filters_disabled === true
  const tree = buildFilterTree<ViewFilter>(view)

  /**
   * Adds a condition to a node, guessing the first thing it could say.
   *
   * The first filterable field and the first condition that field accepts,
   * because a row that arrived empty would be a row somebody has to fill in
   * before it means anything -- and an empty condition is not something the
   * endpoint stores.
   */
  function addFilter(group: ConditionId | null): void {
    const field = filterable[0]
    if (field === undefined) return
    const type = filterTypesFor(field)[0]
    if (type === undefined) return
    add(filterFor(field, type.type), group === null ? null : storedId(group))
  }

  /**
   * How one node combines what is directly inside it.
   *
   * The root is the view itself, which combines by `filter_type` rather than by
   * a group of its own.
   */
  function setConnective(node: FilterNode, next: 'AND' | 'OR'): void {
    const id = node.id === null ? null : storedId(node.id)
    if (id === null) settings({ filter_type: next })
    else changeGroup(id, next)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Filter className="size-4 opacity-70" />
          {all.length === 0
            ? t('view.filter')
            : t('view.filterCount', { count: all.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[36rem] p-2">
        {all.length === 0 && tree.children.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('view.filterNone')}
          </p>
        ) : (
          <ConditionsNode
            node={tree}
            label={t('view.filter')}
            fields={filterable}
            context={context}
            onConnective={setConnective}
            onAddFilter={addFilter}
            onAddGroup={(parent) => {
              addGroup(parent === null ? null : storedId(parent))
            }}
            onChange={(id, changes) => {
              const stored = storedId(id)
              if (stored !== null) change(stored, changes)
            }}
            onRemove={(id) => {
              const stored = storedId(id)
              if (stored !== null) remove(stored)
            }}
            onRemoveGroup={(id) => {
              const stored = storedId(id)
              if (stored !== null) removeGroup(stored)
            }}
          />
        )}

        <div className="mt-2 flex items-center gap-2 border-t pt-2">
          {filterable.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {t('view.filterNoFields')}
            </p>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  addFilter(null)
                }}
              >
                {t('view.filterAdd')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  addGroup(null)
                }}
              >
                {t('view.filterAddGroup')}
              </Button>
            </>
          )}
          {all.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <Label htmlFor="filters-disabled" className="text-xs font-normal">
                {t('view.filterDisabled')}
              </Label>
              <Switch
                id="filters-disabled"
                size="sm"
                checked={disabled}
                onCheckedChange={(next) => {
                  settings({ filters_disabled: next })
                }}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
